import { ProductBundleOption, ProductOption, ProductOptionData } from "@/product-options/option.base";
import { InitSelectorOption } from "@/product-options/option.init-selector";
import { Product, ProductData, UnboundProductOptionData } from "@/products/product.base";

const bundleIds = {
  "smallbs.bs_nadrm.bs_wadc": {
    id    : "b_wa_na",
    coupon: "COUPON.GravityZone-30OFF"
  },
  "smallbs.bs_wadc.bs_nadrm": {
    id    : "b_wa_na",
    coupon: "COUPON.GravityZone-30OFF"
  },
  "smallbs.bs_wadc": {
    id    : "b_wa",
    coupon: "COUPON.GravityZone-30OFF"
  },
  "smallbs.bs_nadrm": {
    id    : "b_na",
    coupon: "COUPON.GravityZone-30OFF"
  }
} as const;

export class InitSelectorProduct extends Product {
  constructor(product: ProductData) {
    super(product);
  }

  protected _createOption(product: ProductOptionData): ProductOption {
    return new InitSelectorOption(product);
  }

  protected async bundle(
    base: ProductOption,
    options: ProductBundleOption[]
  ): Promise<UnboundProductOptionData> {
    // Get the base bundle from common logic.
    const bundleOption = await super.bundle(base, options);

    // First, check for a VPN bundle override.
    if (this.hasVpnOption(base, options.map(o => o.option))) {
      bundleOption.buyLink = this.applyVpnBundle(bundleOption.buyLink);
      return bundleOption;
    }

    // Otherwise, check if there's a small business bundle mapping.
    const mapping = this.getSmallBusinessBundleMapping(base, options.map(o => o.option));
    if (mapping) {
      bundleOption.buyLink = this.applySmallBusinessBundle(
        bundleOption.buyLink,
        mapping
      );
    }

    return bundleOption;
  }

  // Returns true if any option is for the VPN product.
  private hasVpnOption(base: ProductOption, options: ProductOption[]): boolean {
    const isVpnInOptions = options.some(
      option => option.getProduct().getId() === "com.bitdefender.vpn"
    )

    const isBaseVpn = base.getProduct().getId() === "com.bitdefender.vpn";

    return isVpnInOptions || isBaseVpn;
  }

  // For VPN bundles, we adjust the buyLink by replacing "buy" with "buybundle".
  private applyVpnBundle(buyLink: string): string {
    return buyLink.replace("buy", "buybundle");
  }

  // Build the key and lookup a small business bundle mapping, if any.
  private getSmallBusinessBundleMapping(
    base: ProductOption,
    options: ProductOption[]
  ) {
    const baseId = base.getProduct().getId();
    const optionsBundleId = options
      .map(option => option.getProduct().getId())
      .join(".");
    const key = `${baseId}.${optionsBundleId}` as keyof typeof bundleIds;
    return bundleIds[key];
  }

  // Adjust the buyLink using the small business bundle mapping.
  private applySmallBusinessBundle(
    originalBuyLink: string,
    mapping: { id: string; coupon: string }
  ): string {
    // Create a URL from the original buyLink.
    const url = new URL(originalBuyLink, globalThis?.location?.origin);

    // Split the pathname and filter out empty parts.
    const pathParts = url.pathname.split("/").filter(Boolean);

    // Expecting a pathname structure like: [site, store, method, _, devices, subscription, pid?]
    if (pathParts.length < 6) {
      // If the structure is unexpected, return the original link.
      return originalBuyLink;
    }

    // Destructure expected parts.
    const [site, store, method, , devices, subscription, pid] = pathParts;
    // Build the new path with the bundle mapping's id.
    const newPath = `/${[site, store, method, mapping.id, devices, subscription, pid || mapping.coupon].join("/")}`;
    url.pathname = newPath;
    return url.href;
  }
}